import DataWithLabel from '@lyra/core/components/DataWithLabel'
import Heading from '@lyra/core/components/Heading'
import Section from '@lyra/core/components/Section'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatTruncatedNumber from '@lyra/core/utils/formatTruncatedNumber'
import { TokenId } from '@lyra/web/constants/tokens'
import { BoundWalletTransaction } from '@lyra/web/constants/transactions'
import useLyraFundingBalances from '@lyra/web/hooks/useLyraFundingBalances'
import useStakingRewards from '@lyra/web/hooks/useStakingRewards'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import useTransaction from '@lyra/web/hooks/useTransaction'
import { formatTokenBalance } from '@lyra/web/utils/tokens'
import { useCallback, useMemo } from 'react'

import TransactionButtonRow from '../common/TransactionButtonRow'
import DrvPendingUnstakeSectionRows from './DrvPendingUnstakeSectionRows'
import DrvStakeButton from './DrvStakeButton'
import DrvUnstakeButton from './DrvUnstakeButton'

const COL_WIDTH = 180

export default function DrvStakingSection() {
  const { data, isLoading } = useStakingRewards()
  const { fundingBalances } = useLyraFundingBalances()
  const { subaccountDatas } = useSubaccount()
  const { claimRewards } = useTransaction()

  // only staked balance (always in funding wallet)
  const stDrvBalance = fundingBalances.STDRV.balance

  // includes staked balance
  const drvBalance = useMemo(() => {
    const fundingDrvBalance = fundingBalances.DRV.balance + fundingBalances.STDRV.balance
    const subaccountDrvBalance = Object.values(subaccountDatas).reduce(
      (sum, sub) => sum + sub.collateralBalances.DRV.balance,
      0
    )
    return fundingDrvBalance + subaccountDrvBalance
  }, [fundingBalances, subaccountDatas])

  const totalStaked = data?.totalStaked ?? 0
  const circSupply = data?.circulatingSupply ?? 0
  const stakingApy = data?.stakingApy?.STDRV ?? 0
  const pctCircSupplyStaked = circSupply > 0 ? totalStaked / circSupply : 0

  const estWeeklyStakingRewards = (stDrvBalance * stakingApy) / 52
  const claimableStakingRewards = data?.claimableRewards?.STDRV

  const handleClaimRewards: BoundWalletTransaction = useCallback(
    async (options) => {
      if (!claimableStakingRewards) {
        throw new Error('No claimable USDC rewards')
      }
      console.debug('claiming staking rewards', claimableStakingRewards)
      await claimRewards(claimableStakingRewards.batchIds, options)
      // await mutate()
    },
    [claimRewards, claimableStakingRewards]
  )

  return (
    <Section isLarge noTopBorder>
      <Section.Header title="Staking" />
      <Section.Grid minColWidth={COL_WIDTH} maxColWidth={COL_WIDTH}>
        <DataWithLabel
          color={stakingApy > 0 ? 'green' : 'primary'}
          label="Staking APY"
          value={isLoading ? '...' : formatPercentage(stakingApy, { showSign: true })}
        />
        <DataWithLabel
          label="Total Staked"
          value={isLoading ? '...' : `${formatTruncatedNumber(totalStaked)} stDRV`}
        />
        <DataWithLabel
          label="% Circ. Supply Staked"
          value={isLoading ? '...' : formatPercentage(pctCircSupplyStaked)}
        />
      </Section.Grid>
      <Section.Separator />
      <Section.XStack>
        <DataWithLabel
          label="Balance"
          isEditorial
          color="cta"
          value={
            <Heading size="h3" color={drvBalance > 0 ? 'cta' : 'primary'}>
              {formatTokenBalance(drvBalance, TokenId.DRV)}
            </Heading>
          }
        />
      </Section.XStack>
      <Section.Grid minColWidth={COL_WIDTH} maxColWidth={COL_WIDTH}>
        <DataWithLabel
          label="Amount Staked"
          value={formatTokenBalance(stDrvBalance, TokenId.STDRV)}
        />
        <DataWithLabel
          label="Claimable Rewards"
          value={
            isLoading
              ? '...'
              : formatTokenBalance(claimableStakingRewards?.amount ?? 0, TokenId.STDRV, {
                  truncate: true,
                })
          }
        />
        <DataWithLabel
          label="Est. Weekly Rewards"
          value={`+${formatTokenBalance(estWeeklyStakingRewards, TokenId.STDRV, {
            truncate: true,
          })}`}
        />
      </Section.Grid>
      <Section.Grid minColWidth={180} maxColWidth={180}>
        {claimableStakingRewards ? (
          <TransactionButtonRow
            label="Claim Rewards"
            reviewTitle="Claim Rewards"
            loggerAction="claim-rewards"
            onSubmitTransaction={handleClaimRewards}
            noHorizontalPadding
          />
        ) : null}
        <DrvStakeButton isCta={!claimableStakingRewards && !stDrvBalance} size="lg" />
        <DrvUnstakeButton size="lg" />
      </Section.Grid>
      <DrvPendingUnstakeSectionRows />
    </Section>
  )
}
